import React, { useState, useEffect } from 'react';
import FourPillars from '../components/FourPillars';

const ITADPage = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [activeSection, setActiveSection] = useState('');

  const slideBgStyle = {
    backgroundImage: 'linear-gradient(120deg, rgba(15,23,42,0.9), rgba(20,184,166,0.85)), url(/it_disposition.jpg)',
    backgroundPosition: 'center',
    backgroundSize: 'cover',
    backgroundRepeat: 'no-repeat',
  };

  const StepIcon = ({ step }) => {
    const iconSize = 140;
    const common = {
      width: iconSize,
      height: iconSize,
      viewBox: '0 0 140 140',
      fill: 'none',
    };
    
    switch (step) {
      case 1:
        return (
          <svg {...common}>
            <rect x="20" y="20" width="100" height="100" rx="12" stroke="currentColor" strokeWidth="4" fill="none"/>
            <path d="M20 20 L30 30 M120 20 L110 30 M20 120 L30 110 M120 120 L110 110" stroke="currentColor" strokeWidth="3" strokeLinecap="round"/>
            <rect x="40" y="50" width="60" height="40" rx="4" stroke="currentColor" strokeWidth="3" fill="none"/>
            <path d="M50 65h40M50 80h30" stroke="currentColor" strokeWidth="3" strokeLinecap="round"/>
            <circle cx="45" cy="95" r="6" fill="currentColor"/>
            <circle cx="95" cy="95" r="6" fill="currentColor"/>
          </svg>
        );
      case 2:
        return (
          <svg {...common}>
            <rect x="20" y="20" width="100" height="100" rx="12" stroke="currentColor" strokeWidth="4" fill="none"/>
            <path d="M20 20 L30 30 M120 20 L110 30 M20 120 L30 110 M120 120 L110 110" stroke="currentColor" strokeWidth="3" strokeLinecap="round"/>
            <rect x="45" y="45" width="50" height="50" rx="4" stroke="currentColor" strokeWidth="3" fill="none"/>
            <path d="M55 60h30M55 75h30M55 90h20" stroke="currentColor" strokeWidth="3" strokeLinecap="round"/>
            <path d="M80 90l5 5 10-10" stroke="currentColor" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
          </svg>
        );
      case 3:
        return (
          <svg {...common}>
            <rect x="20" y="20" width="100" height="100" rx="12" stroke="currentColor" strokeWidth="4" fill="none"/>
            <path d="M20 20 L30 30 M120 20 L110 30 M20 120 L30 110 M120 120 L110 110" stroke="currentColor" strokeWidth="3" strokeLinecap="round"/>
            <circle cx="50" cy="50" r="15" stroke="currentColor" strokeWidth="3" fill="none"/>
            <circle cx="90" cy="50" r="15" stroke="currentColor" strokeWidth="3" fill="none"/>
            <circle cx="50" cy="90" r="15" stroke="currentColor" strokeWidth="3" fill="none"/>
            <circle cx="90" cy="90" r="15" stroke="currentColor" strokeWidth="3" fill="none"/>
            <path d="M65 50h25M50 65v25M90 65v25M65 90h25" stroke="currentColor" strokeWidth="3"/>
            <circle cx="70" cy="70" r="8" fill="currentColor"/>
          </svg>
        );
      case 4:
        return (
          <svg {...common}>
            <rect x="20" y="20" width="100" height="100" rx="12" stroke="currentColor" strokeWidth="4" fill="none"/>
            <path d="M20 20 L30 30 M120 20 L110 30 M20 120 L30 110 M120 120 L110 110" stroke="currentColor" strokeWidth="3" strokeLinecap="round"/>
            <rect x="35" y="35" width="70" height="70" rx="6" stroke="currentColor" strokeWidth="3" fill="none"/>
            <path d="M45 55h50M45 70h50M45 85h40" stroke="currentColor" strokeWidth="3" strokeLinecap="round"/>
            <path d="M80 85l8 8 16-16" stroke="currentColor" strokeWidth="5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
          </svg>
        );
      default:
        return null;
    }
  };

  const steps = [
    {
      step: 1,
      title: 'Secure Chain-of-Custody & Logistics',
      description: 'Protect assets with secure, tracked logistics (GPS, vetted personnel, monitored facilities) from pickup to disposition, providing full audit trails.',
    },
    {
      step: 2,
      title: 'Certified Data Destruction',
      description: 'Ensure data elimination via NIST 800-88 sanitization or secure shredding, with verification and destruction certificates provided for each device.',
    },
    {
      step: 3,
      title: 'Value Recovery & Remarketing',
      description: 'Maximize resale value via specialized B2B channels and refurbishment; recover value from non-functional units through component/commodity harvesting.',
    },
    {
      step: 4,
      title: 'Environmental Compliance & Reporting',
      description: 'Comply with R2/e-Stewards/ISO 14001 via zero-landfill policy, responsible waste handling, downstream tracking, and detailed ESG impact reports.',
    },
  ];

  const goToStep = (index) => {
    setCurrentStep(index);
  };

  useEffect(() => {
    const handleScroll = () => {
      const sections = [
        'secure-disposal',
        'security-challenge',
        'itad-methodology',
        'regulatory-compliance',
        'value-recovery',
        'equipment-categories'
      ];
      
      const scrollPosition = window.scrollY + 150;
      
      for (let i = sections.length - 1; i >= 0; i--) {
        const section = document.getElementById(sections[i]);
        if (section && section.offsetTop <= scrollPosition) {
          setActiveSection(sections[i]);
          break;
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const offset = 100;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
      setActiveSection(sectionId);
    }
  };

  return (
    <main className="services">
      <div className="container">
        <div className="sv-content-wrapper">
          <div className="sv-main-content">
            {/* Hero */}
            <section className="sv-hero">
              <div className="sv-eyebrow">IT Asset Disposition</div>
              <h1 className="sv-title">IT Asset Disposition & Recycling</h1>
              <p className="sv-subtitle">Securely retire corporate IT assets with certified data destruction, compliant processing, and maximum value recovery.</p>
            </section>

            {/* 4-Step Process Carousel */}
            <section className="ti-steps-carousel">
              <div className="ti-carousel-slide-container">
                <div 
                  className="ti-carousel-slides" 
                  style={{ transform: `translateX(-${currentStep * 100}%)` }}
                >
                  {steps.map((step, index) => (
                    <div key={step.step} className="ti-carousel-slide" style={slideBgStyle}>
                      <div className="ti-carousel-icon">
                        <StepIcon step={step.step} />
                      </div>
                      <div className="ti-carousel-content">
                        <div className="ti-step-badge">STEP {step.step}</div>
                        <h3>{step.title}</h3>
                        <p>{step.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="ti-carousel-indicators">
                {steps.map((_, index) => (
                  <button
                    key={index}
                    className={`ti-carousel-indicator ${index === currentStep ? 'active' : ''}`}
                    onClick={() => goToStep(index)}
                    aria-label={`Go to step ${index + 1}`}
                  />
                ))}
              </div>
            </section>

            {/* Secure IT Asset Disposal Section */}
            <section id="secure-disposal" className="sv-overview">
              <h2 className="sv-section-title">Secure IT Asset Disposal for Modern Enterprises</h2>
              <p style={{ fontSize: '18px', lineHeight: '1.7', color: '#334155', marginBottom: '24px' }}>
                43% of second-hand storage media contains recoverable sensitive data despite conventional erasure attempts. With regulatory penalties now reaching up to 4% of global revenue under frameworks like GDPR and HIPAA, proper IT asset disposition is no longer optional—it's essential risk management.
              </p>
              <p style={{ fontSize: '18px', lineHeight: '1.7', color: '#334155', marginBottom: '24px' }}>
                Our comprehensive ITAD program provides military-grade data destruction, certified environmental processing, and maximum value recovery in one seamless service. Unlike conventional recycling or donations, our approach creates a documented, defensible process that protects both your data and your compliance posture.
              </p>
            </section>

            {/* Multi-dimensional Security Challenge */}
            <section id="security-challenge" className="sv-overview">
              <h2 className="sv-section-title">The Multi-dimensional Security Challenge</h2>
              <p style={{ fontSize: '18px', lineHeight: '1.7', color: '#334155', marginBottom: '32px' }}>
                Modern IT asset disposal must address three interconnected security dimensions:
              </p>
              <div className="sv-features-grid">
                <div className="sv-feature">
                  <h4>Data Security</h4>
                  <p>From overlooked storage devices to unexpected data repositories, comprehensive identification and destruction of all data-bearing components is essential.</p>
                </div>
                <div className="sv-feature">
                  <h4>Chain-of-Custody Security</h4>
                  <p>Assets must remain in secured, monitored environments from pickup to final processing to prevent theft, tampering, or unauthorized access.</p>
                </div>
                <div className="sv-feature">
                  <h4>Documentation Security</h4>
                  <p>Without proper verification records, even properly sanitized devices leave organizations exposed to compliance challenges and inability to demonstrate due diligence.</p>
                </div>
              </div>
              <p style={{ fontSize: '18px', lineHeight: '1.7', color: '#334155', marginTop: '24px' }}>
                Each dimension requires specialized expertise and infrastructure—precisely what our ITAD program delivers through our four-phase process.
              </p>
            </section>

            {/* Our ITAD Methodology */}
            <section id="itad-methodology" className="sv-overview">
              <h2 className="sv-section-title">Our ITAD Methodology</h2>
              <p style={{ fontSize: '18px', lineHeight: '1.7', color: '#334155', marginBottom: '32px' }}>
                The security and environmental impact of asset disposition depends entirely on the methodology used. Our approach combines military-grade protocols with environmental responsibility:
              </p>
              
              <div style={{ marginBottom: '32px' }}>
                <h3 style={{ fontSize: '22px', fontWeight: '700', color: '#0f172a', marginBottom: '12px' }}>Secure Collection & Transport</h3>
                <p style={{ fontSize: '16px', lineHeight: '1.7', color: '#334155', marginBottom: '24px' }}>
                  Our dedicated logistics team uses GPS-tracked vehicles and background-checked personnel to ensure complete chain-of-custody protection. Every asset receives tamper-evident secure tagging at the moment of pickup, initiating our continuous monitoring until final processing. For sensitive environments, our secure transport units provide on-site data destruction before devices ever leave your premises.
                </p>
              </div>

              <div style={{ marginBottom: '32px' }}>
                <h3 style={{ fontSize: '22px', fontWeight: '700', color: '#0f172a', marginBottom: '12px' }}>Inventory & Assessment</h3>
                <p style={{ fontSize: '16px', lineHeight: '1.7', color: '#334155', marginBottom: '24px' }}>
                  In our secure processing facility, each asset undergoes detailed cataloging with make, model, serial number, and condition documentation. Our advanced inventory system integrates with your asset management records to reconcile decommissioned units against your database. This stage includes identifying residual value potential and appropriate disposition pathways for each item.
                </p>
              </div>

              <div style={{ marginBottom: '32px' }}>
                <h3 style={{ fontSize: '22px', fontWeight: '700', color: '#0f172a', marginBottom: '12px' }}>Data Sanitization & Destruction</h3>
                <p style={{ fontSize: '16px', lineHeight: '1.7', color: '#334155', marginBottom: '24px' }}>
                  Data-bearing components follow one of two security pathways: NIST 800-88 compliant software sanitization with verification or physical destruction. Hard drives undergo a three-phase overwrite process followed by verification sampling, or precision shredding when physical destruction is required. Each device receives individual certification with method documentation and operator verification.
                </p>
              </div>

              <div style={{ marginBottom: '32px' }}>
                <h3 style={{ fontSize: '22px', fontWeight: '700', color: '#0f172a', marginBottom: '12px' }}>Value Recovery & Processing</h3>
                <p style={{ fontSize: '16px', lineHeight: '1.7', color: '#334155', marginBottom: '24px' }}>
                  Our zero-landfill processing routes equipment to appropriate remarketing, component recovery, or materials reclamation pathways. Functional equipment enters our refurbishment center for data wiping, testing, repair, and remarketing. Non-functional items proceed to component harvesting or materials separation with full downstream tracking through final processing.
                </p>
              </div>
            </section>

            {/* Regulatory Compliance Matrix */}
            <section id="regulatory-compliance" className="sv-overview">
              <h2 className="sv-section-title">Regulatory Compliance Matrix</h2>
              <p style={{ fontSize: '18px', lineHeight: '1.7', color: '#334155', marginBottom: '24px' }}>
                The regulatory landscape for IT asset disposition becomes more complex each year. Our program addresses requirements across multiple frameworks:
              </p>
              <div style={{ overflowX: 'auto', marginBottom: '32px' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid #e5e7eb', borderRadius: '8px', overflow: 'hidden' }}>
                  <thead>
                    <tr style={{ background: '#f8fafc' }}>
                      <th style={{ padding: '12px 16px', textAlign: 'left', borderBottom: '1px solid #e5e7eb', fontWeight: '600', color: '#0f172a' }}>Regulatory Area</th>
                      <th style={{ padding: '12px 16px', textAlign: 'left', borderBottom: '1px solid #e5e7eb', fontWeight: '600', color: '#0f172a' }}>Key Frameworks</th>
                      <th style={{ padding: '12px 16px', textAlign: 'left', borderBottom: '1px solid #e5e7eb', fontWeight: '600', color: '#0f172a' }}>Our Compliance Approach</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td style={{ padding: '12px 16px', borderBottom: '1px solid #e5e7eb', color: '#334155' }}>Data Privacy</td>
                      <td style={{ padding: '12px 16px', borderBottom: '1px solid #e5e7eb', color: '#334155' }}>GDPR, CCPA, HIPAA, GLBA</td>
                      <td style={{ padding: '12px 16px', borderBottom: '1px solid #e5e7eb', color: '#334155' }}>Device-level destruction certificates with sanitization method verification</td>
                    </tr>
                    <tr>
                      <td style={{ padding: '12px 16px', borderBottom: '1px solid #e5e7eb', color: '#334155' }}>Environmental</td>
                      <td style={{ padding: '12px 16px', borderBottom: '1px solid #e5e7eb', color: '#334155' }}>EPA, WEEE Directive</td>
                      <td style={{ padding: '12px 16px', borderBottom: '1px solid #e5e7eb', color: '#334155' }}>Documented zero-landfill processing with downstream tracking</td>
                    </tr>
                    <tr>
                      <td style={{ padding: '12px 16px', borderBottom: '1px solid #e5e7eb', color: '#334155' }}>Industry-Specific</td>
                      <td style={{ padding: '12px 16px', borderBottom: '1px solid #e5e7eb', color: '#334155' }}>PCI-DSS, SOC 2, CMMC</td>
                      <td style={{ padding: '12px 16px', borderBottom: '1px solid #e5e7eb', color: '#334155' }}>Customized protocols matching specific framework requirements</td>
                    </tr>
                    <tr>
                      <td style={{ padding: '12px 16px', color: '#334155' }}>Governance</td>
                      <td style={{ padding: '12px 16px', color: '#334155' }}>ISO 27001, NIST CSF</td>
                      <td style={{ padding: '12px 16px', color: '#334155' }}>Process alignment with broader security frameworks</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p style={{ fontSize: '18px', lineHeight: '1.7', color: '#334155' }}>
                This unified approach streamlines compliance management across multiple requirements, reducing the documentation burden while improving security outcomes.
              </p>
            </section>

            {/* Maximizing Value Recovery */}
            <section id="value-recovery" className="sv-overview">
              <h2 className="sv-section-title">Maximizing Value Recovery</h2>
              
              <div style={{ marginBottom: '32px' }}>
                <h3 style={{ fontSize: '22px', fontWeight: '700', color: '#0f172a', marginBottom: '12px' }}>Functional Equipment Remarketing</h3>
                <p style={{ fontSize: '16px', lineHeight: '1.7', color: '#334155', marginBottom: '24px' }}>
                  Through our specialized B2B channels, your functional equipment reaches targeted buyers seeking specific configurations. This direct-to-user approach typically returns 40-60% more value than conventional liquidation methods, with all proceeds transparently tracked and credited.
                </p>
              </div>

              <div style={{ marginBottom: '32px' }}>
                <h3 style={{ fontSize: '22px', fontWeight: '700', color: '#0f172a', marginBottom: '12px' }}>Refurbishment & Repair Services</h3>
                <p style={{ fontSize: '16px', lineHeight: '1.7', color: '#334155', marginBottom: '24px' }}>
                  Our certified technicians can upgrade, repair, and recertify equipment to maximize resale potential. By addressing common issues like storage upgrades, battery replacement, and cosmetic refurbishment, we can substantially increase recovery value for aging assets.
                </p>
              </div>

              <div style={{ marginBottom: '32px' }}>
                <h3 style={{ fontSize: '22px', fontWeight: '700', color: '#0f172a', marginBottom: '12px' }}>Component Recovery Operations</h3>
                <p style={{ fontSize: '16px', lineHeight: '1.7', color: '#334155', marginBottom: '24px' }}>
                  When full systems no longer hold market value, our component-level recovery extracts still-valuable elements like memory modules, processors, expansion cards, and power supplies for the secondary market, recovering value from otherwise non-marketable units.
                </p>
              </div>

              <div style={{ marginBottom: '32px' }}>
                <h3 style={{ fontSize: '22px', fontWeight: '700', color: '#0f172a', marginBottom: '12px' }}>Materials Reclamation Processing</h3>
                <p style={{ fontSize: '16px', lineHeight: '1.7', color: '#334155', marginBottom: '24px' }}>
                  At the final stage, our precision separation processes recover precious metals, rare earth elements, and base metals from electronic components, ensuring both environmental compliance and maximum commodity value recovery.
                </p>
              </div>

              <p style={{ fontSize: '18px', lineHeight: '1.7', color: '#334155', fontWeight: '600' }}>
                On average, our clients recover 18% of original purchase value—substantially offsetting disposition costs and supporting technology refresh initiatives.
              </p>
            </section>

            {/* Equipment Categories */}
            <section id="equipment-categories" className="sv-overview">
              <h2 className="sv-section-title">Equipment Categories We Process</h2>
              <p style={{ fontSize: '18px', lineHeight: '1.7', color: '#334155', marginBottom: '24px' }}>
                Our comprehensive service handles the complete spectrum of corporate technology assets:
              </p>
              <div style={{ overflowX: 'auto', marginBottom: '32px' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid #e5e7eb', borderRadius: '8px', overflow: 'hidden' }}>
                  <thead>
                    <tr style={{ background: '#f8fafc' }}>
                      <th style={{ padding: '12px 16px', textAlign: 'left', borderBottom: '1px solid #e5e7eb', fontWeight: '600', color: '#0f172a' }}>Category</th>
                      <th style={{ padding: '12px 16px', textAlign: 'left', borderBottom: '1px solid #e5e7eb', fontWeight: '600', color: '#0f172a' }}>Included Items</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td style={{ padding: '12px 16px', borderBottom: '1px solid #e5e7eb', color: '#334155', fontWeight: '600' }}>Computing Equipment</td>
                      <td style={{ padding: '12px 16px', borderBottom: '1px solid #e5e7eb', color: '#334155' }}>Desktops, laptops, tablets, servers, workstations</td>
                    </tr>
                    <tr>
                      <td style={{ padding: '12px 16px', borderBottom: '1px solid #e5e7eb', color: '#334155', fontWeight: '600' }}>Networking Equipment</td>
                      <td style={{ padding: '12px 16px', borderBottom: '1px solid #e5e7eb', color: '#334155' }}>Routers, switches, firewalls, access points, cabling</td>
                    </tr>
                    <tr>
                      <td style={{ padding: '12px 16px', borderBottom: '1px solid #e5e7eb', color: '#334155', fontWeight: '600' }}>Data Center Equipment</td>
                      <td style={{ padding: '12px 16px', borderBottom: '1px solid #e5e7eb', color: '#334155' }}>Rack servers, storage arrays, UPS systems, PDUs</td>
                    </tr>
                    <tr>
                      <td style={{ padding: '12px 16px', borderBottom: '1px solid #e5e7eb', color: '#334155', fontWeight: '600' }}>Mobile Devices</td>
                      <td style={{ padding: '12px 16px', borderBottom: '1px solid #e5e7eb', color: '#334155' }}>Smartphones, cell phones, enterprise mobile computers</td>
                    </tr>
                    <tr>
                      <td style={{ padding: '12px 16px', borderBottom: '1px solid #e5e7eb', color: '#334155', fontWeight: '600' }}>Storage Media</td>
                      <td style={{ padding: '12px 16px', borderBottom: '1px solid #e5e7eb', color: '#334155' }}>Hard drives, SSDs, tape drives, USB drives</td>
                    </tr>
                    <tr>
                      <td style={{ padding: '12px 16px', borderBottom: '1px solid #e5e7eb', color: '#334155', fontWeight: '600' }}>Peripherals</td>
                      <td style={{ padding: '12px 16px', borderBottom: '1px solid #e5e7eb', color: '#334155' }}>Monitors, keyboards, mice, scanners, webcams</td>
                    </tr>
                    <tr>
                      <td style={{ padding: '12px 16px', borderBottom: '1px solid #e5e7eb', color: '#334155', fontWeight: '600' }}>Printing Equipment</td>
                      <td style={{ padding: '12px 16px', borderBottom: '1px solid #e5e7eb', color: '#334155' }}>Printers, copiers, multi‑function devices</td>
                    </tr>
                    <tr>
                      <td style={{ padding: '12px 16px', borderBottom: '1px solid #e5e7eb', color: '#334155', fontWeight: '600' }}>Communication Systems</td>
                      <td style={{ padding: '12px 16px', borderBottom: '1px solid #e5e7eb', color: '#334155' }}>IP phones, conference systems, PBX equipment</td>
                    </tr>
                    <tr>
                      <td style={{ padding: '12px 16px', borderBottom: '1px solid #e5e7eb', color: '#334155', fontWeight: '600' }}>Point of Sale Equipment</td>
                      <td style={{ padding: '12px 16px', borderBottom: '1px solid #e5e7eb', color: '#334155' }}>Terminals, barcode scanners, receipt printers</td>
                    </tr>
                    <tr>
                      <td style={{ padding: '12px 16px', color: '#334155', fontWeight: '600' }}>Security Hardware</td>
                      <td style={{ padding: '12px 16px', color: '#334155' }}>Security cameras, access‑control systems, card readers</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p style={{ fontSize: '18px', lineHeight: '1.7', color: '#334155' }}>
                With specialized handling protocols for each category and security classification, we ensure appropriate processing regardless of device type or sensitivity level.
              </p>
            </section>

            {/* Four Pillars */}
            <FourPillars />

            {/* CTA */}
            <section className="sv-cta">
              <div className="sv-cta-inner">
                <h3>Ready to maximize your IT asset value?</h3>
                <p>Get a free assessment and discover how much value you can recover.</p>
                <div className="sv-cta-actions">
                  <a className="btn btn-primary" href="tel:+18778739336">Call (877) 873-9336</a>
                  <a className="btn btn-secondary" href="mailto:info@globoflo.com">Email Us</a>
                </div>
              </div>
            </section>
          </div>

          {/* Quick Links Sidebar */}
          <aside className="sv-quick-links">
            <h3 className="sv-quick-links-title">IT Asset Disposition & Recycling</h3>
            <nav className="sv-quick-links-nav">
              <ul>
                <li>
                  <button 
                    className={activeSection === 'secure-disposal' ? 'active' : ''}
                    onClick={() => scrollToSection('secure-disposal')}
                  >
                    Secure IT Asset Disposal for Modern Enterprises
                  </button>
                </li>
                <li>
                  <button 
                    className={activeSection === 'security-challenge' ? 'active' : ''}
                    onClick={() => scrollToSection('security-challenge')}
                  >
                    The Multi-dimensional Security Challenge
                  </button>
                </li>
                <li>
                  <button 
                    className={activeSection === 'itad-methodology' ? 'active' : ''}
                    onClick={() => scrollToSection('itad-methodology')}
                  >
                    Our ITAD Methodology
                  </button>
                </li>
                <li>
                  <button 
                    className={activeSection === 'regulatory-compliance' ? 'active' : ''}
                    onClick={() => scrollToSection('regulatory-compliance')}
                  >
                    Regulatory Compliance Matrix
                  </button>
                </li>
                <li>
                  <button 
                    className={activeSection === 'value-recovery' ? 'active' : ''}
                    onClick={() => scrollToSection('value-recovery')}
                  >
                    Maximizing Value Recovery
                  </button>
                </li>
                <li>
                  <button 
                    className={activeSection === 'equipment-categories' ? 'active' : ''}
                    onClick={() => scrollToSection('equipment-categories')}
                  >
                    Equipment Categories We Process
                  </button>
                </li>
              </ul>
            </nav>
          </aside>
        </div>
      </div>
    </main>
  );
};

export default ITADPage;
